import {
  AfterViewInit,
  Component,
  Inject,
  Input,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { StopFullData } from 'src/app/modules/route-planner/interfaces/route-planning/service/@schedule-service.interface';
import { BaseMap } from '../../../../../shared/map/base-map.component';
import { FavoritesService } from 'src/app/modules/route-planner/services/favorites/favorites.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import {
  MapService,
  MarkersClustering,
} from 'src/app/modules/route-planner/services/map/map.service';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { Overlay } from '@angular/cdk/overlay';
import { DATA_INJECTION_TOKEN } from 'src/app/@shared/services/overlay/overlay.service';
import { of } from 'rxjs';
import { MAP_ICONS } from 'src/app/modules/route-planner/constants/map-icons.constants';
import { eMapLocationType } from 'src/app/modules/route-planner/enums/route-planning/location-type.enum';
import { StationsAutoCompleteMapMarkerComponent } from '../../../../../route-planner-form/forms/search-station-form/stations-auto-complete/stations-auto-complete-map/stations-auto-complete-map-marker/stations-auto-complete-map-marker.component';
import { MapComponent } from '../../../../../shared/map/map.component';
import { SearchStopForm } from 'src/app/modules/route-planner/interfaces/route-planning/forms/search-stop-form';
import { QueryParamsService } from 'src/app/@shared/services/query-params/query-params.service';
import { SearchStationDetailedResultComponent } from '../../search-station-detailed-result/search-station-detailed-result.component';

@Component({
    selector: 'app-search-stations-result-map',
    templateUrl: './search-stations-result-map.component.html',
    styleUrl: './search-stations-result-map.component.scss',
    providers: [MapService],
    standalone: false
})
export class SearchStationsResultMapComponent
  extends BaseMap<StopFullData>
  implements OnInit, AfterViewInit {
  @ViewChild(MapComponent) override mapComponent?: MapComponent;
  @Input() data: {
    formValues: SearchStopForm;
    stations: StopFullData[];
  };

  @ViewChild('mapRef', { read: ViewContainerRef })
  mapContainerRef: ViewContainerRef;

  activeStationMarker: H.map.Marker;
  activeStationDetailsMarker: H.map.DomMarker;
  stationCodeText: string;

  constructor(
    favoritesService: FavoritesService,
    resizeService: ResizeService,
    overlay: Overlay,
    mapService: MapService,
    routePlanningService: RoutePlanningService,
    private readonly queryParamsService: QueryParamsService,

    @Inject(DATA_INJECTION_TOKEN)
    public readonly injectedData: {
      formValues: SearchStopForm;
      stations: StopFullData[];
    }
  ) {
    super(
      favoritesService,
      resizeService,
      overlay,
      mapService,
      routePlanningService
    );
  }

  override ngOnInit(): void {
    if (!!this.injectedData) this.data = this.injectedData;
    this.routePlanningService.setMapService(this.mapService);
    this.routePlanningService._resultSubject.subscribe((result) => {
      this.focusStation(result.value);
    });
  }

  override ngAfterViewInit(): void {
    this.mapService.setViewContainerRef(this.mapContainerRef);

    this._mapInitialized = this.mapService.initializeMap(
      this.mapComponent.mapDiv,
      {
        focusOnUserLocation: false,
        controls: {
          zoom: true,
          myLocation: true,
        },
        markersClustering: this.getMarkersClustering(this.data.stations),
      }
    );
    this.mapService.$mapInitialized.subscribe((map) => {
      this._mapInitialized = true;

      map.addEventListener('tap', (ev: any) => {
        ev.preventDefault();

        // Click on marker / DomMarker / cluster - let them handle it
        if (ev.target instanceof H.map.Marker || ev.target instanceof H.map.DomMarker) {
          return;
        }

        // Click on empty map area - close active station
        if (!!this.activeStationDetailsMarker) {
          this.mapService.removeObject(this.activeStationDetailsMarker);
          this.activeStationDetailsMarker = null;
        }

        this.setMarkerIcon(this.activeStationMarker, MAP_ICONS.station());
        this.activeStationMarker = null;
      });

      const poi = this.data.formValues.poi;
      this.mapService.updateMapView(poi.lat, poi.lng, 15, true);
    });
  }

  private setMarkerIcon(marker: H.map.Marker | null, icon: H.map.Icon) {
    if (marker) {
      marker.setIcon(icon);
    }
  }

  focusStation(stop?: StopFullData) {
    if (!!stop) {
      const activeStation =
        this.activeStationDetailsMarker?.getData() as StopFullData;
      const stopLocation = { lat: stop.lat, lng: stop.lng };

      if (stop.stopcode !== activeStation?.stopcode) {
        this.mapService.removeObject(this.activeStationDetailsMarker);
        this.setMarkerIcon(this.activeStationMarker, MAP_ICONS.station());

        const container = this.mapService.createMapObject<
          StationsAutoCompleteMapMarkerComponent,
          any
        >(StationsAutoCompleteMapMarkerComponent, { stop: stop, showPickStationButton: true });
        const icon: H.map.DomIcon = new H.map.DomIcon(container);

        this.activeStationMarker = this.mapService.mapObjects.find((object) => {
          const data = object.getData() as StopFullData;
          return data?.stopcode === stop.stopcode;
        }) as H.map.Marker;

        this.setMarkerIcon(this.activeStationMarker, MAP_ICONS.selected_station());

        this.activeStationDetailsMarker = this.mapService.addDomMarker(
          stopLocation,
          icon,
          stop
        );

        setTimeout(() => {
          this.mapService.updateMapView(stop.lat, stop.lng, 18, true);
        }, 50);
      }
    } else {
      this.mapService.removeObject(this.activeStationDetailsMarker);
      this.setMarkerIcon(this.activeStationMarker, MAP_ICONS.station());
      this.activeStationMarker = null;
      this.activeStationDetailsMarker = null;
    }
  }

  getMarkersClustering(stops: StopFullData[]): MarkersClustering {
    const options = {
      eps: 3,
      minWeight: 50,
    };

    return {
      layers: [
        {
          dataPoints: stops.map((stop) => ({
            type: eMapLocationType.STATION,
            coordinates: { lat: stop.lat, lng: stop.lng },
            data: stop,
          })),
          dataPointObjectFactory: (dataPoint) => {
            if (!dataPoint.isCluster()) {
              const raw = dataPoint.getData();
              const stop = raw.data as StopFullData;
              
              // Use dataPoint.getPosition() as in working components!
              const marker = new H.map.Marker(dataPoint.getPosition(), {
                data: stop,
                min: dataPoint.getMinZoom(),
                icon: MAP_ICONS.station(),
              });

              marker.addEventListener('tap', (ev) => {
                ev.stopPropagation();
                const targetMarker = ev.target as H.map.Marker;
                const clickedStation = targetMarker.getData() as StopFullData;
                const stopLocation = dataPoint.getPosition();
                const activeStation =
                  this.activeStationDetailsMarker?.getData() as StopFullData;
                this.setMarkerIcon(this.activeStationMarker, MAP_ICONS.station());
                this.mapService.removeObject(this.activeStationDetailsMarker);

                if (activeStation?.stopcode !== clickedStation.stopcode) {
                  const container = this.mapService.createMapObject<
                    StationsAutoCompleteMapMarkerComponent,
                    any
                  >(StationsAutoCompleteMapMarkerComponent, { stop: clickedStation, showPickStationButton: true });
                  const icon: H.map.DomIcon = new H.map.DomIcon(container, {
                      onAttach: (element) => {
                      const button = element.getElementsByClassName('choose-station').item(0);
                      if (button) {
                        button.addEventListener('click', (e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          this.pickStation(clickedStation)
                        });
                      } else {
                        console.warn('Button not found in marker DOM');
                      }

                      const closeButton = element.getElementsByClassName('close-station-data').item(0);
                      if (closeButton) {
                        closeButton.addEventListener('click', (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                      

                          this.mapService.removeObject(this.activeStationDetailsMarker)
                          this.activeStationMarker = null;
                          this.activeStationDetailsMarker = null;
                        });
                      } else {
                        console.warn('closeButton not found in marker DOM');
                      }
                    }
                  });

                  this.setMarkerIcon(targetMarker, MAP_ICONS.selected_station());
                  this.activeStationDetailsMarker =
                    this.mapService.addDomMarker(
                      stopLocation,
                      icon,
                      clickedStation
                    );
                  this.mapService.updateMapView(clickedStation.lat, clickedStation.lng, 18, true);
                  this.activeStationMarker = targetMarker;
                } else {
                  this.activeStationMarker = null;
                  this.activeStationDetailsMarker = null;
                }
              });

              return marker;
            }

            return null;
          },
          options: options,
        },
      ],
    };
  }

  pickStation(station: StopFullData) {
      // Find the index of the selected station
      const selectedIndex = this.data.stations.findIndex(
        (s) => s.stopcode === station.stopcode
      );
  
      // Add the selected index to query params if found
      if (selectedIndex !== -1) {
        this.queryParamsService.setQueryParam('poiSelectedIndex', selectedIndex);
      }
  
      this.routePlanningService._resultSubject.next({
        type: 'selected',
        value: station,
      });
      this.routePlanningService._componentOverlaySub.next({
        componentType: SearchStationDetailedResultComponent,
        data: {
          stop: station,
          formValues: this.data.formValues,
          closeCallbackFn: (isCloseStationList) => {
          
          }
        },
      });
    }
}
